import { json } from '@codemirror/lang-json';
import { xml } from '@codemirror/lang-xml';
import {
	defaultHighlightStyle,
	foldGutter,
	syntaxHighlighting
} from '@codemirror/language';
import { Compartment, EditorState } from '@codemirror/state';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import { useTheme } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { PropsWithTestId } from 'types/props';
import xmlFormatter from 'xml-formatter';

interface CodeViewerProps extends PropsWithTestId {
	content: string;
	language: 'json' | 'xml';
	maxHeight?: number;
}

const CodeViewer: React.FC<CodeViewerProps> = ({
	content,
	language,
	maxHeight = undefined,
	'data-testid': dataTestId
}) => {
	const {
		palette: { mode }
	} = useTheme();

	const formattedData = useMemo(() => {
		try {
			switch (language) {
				case 'json':
					return JSON.stringify(JSON.parse(content), null, 2);
				case 'xml':
					return xmlFormatter(content, { indentation: '  ' });
				default:
					/* istanbul ignore next: should not happen currently  */
					return content;
			}
		} catch {
			return content;
		}
	}, [content, language]);

	const containerRef = useRef<HTMLDivElement>(null);
	const viewRef = useRef<EditorView | null>(null);
	const themeCompartment = useRef(new Compartment());
	const langCompartment = useRef(new Compartment());
	const heightCompartment = useRef(new Compartment());

	// Create the EditorView once on mount, destroy on unmount
	useEffect(() => {
		if (!containerRef.current) return () => {};

		viewRef.current = new EditorView({
			state: EditorState.create({
				doc: formattedData,
				extensions: [
					EditorState.readOnly.of(true),
					EditorView.editable.of(false),
					// CodeMirror mounts its themes as <style> elements, which a
					// strict style-src rejects unless they carry the request nonce.
					// Undefined when ADD_CSP is off, where the policy has no nonce.
					EditorView.cspNonce.of((window as any).DESCOPE_NONCE ?? ''),
					foldGutter(),
					langCompartment.current.of(xml()),
					themeCompartment.current.of(mode === 'dark' ? oneDark : []),
					// 3. Highlighting last (and only as a fallback for light mode)
					mode === 'light' ? syntaxHighlighting(defaultHighlightStyle) : [],
					heightCompartment.current.of(
						EditorView.theme({
							'.cm-scroller': { overflow: 'auto' }
						})
					)
				]
			}),
			parent: containerRef.current
		});

		return () => {
			viewRef.current?.destroy();
			viewRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Update document content
	useEffect(() => {
		const view = viewRef.current;
		if (!view) return;
		const currentDoc = view.state.doc.toString();
		if (currentDoc !== formattedData) {
			view.dispatch({
				changes: { from: 0, to: view.state.doc.length, insert: formattedData }
			});
		}
	}, [formattedData]);

	// Update language
	useEffect(() => {
		viewRef.current?.dispatch({
			effects: langCompartment.current.reconfigure(
				language === 'json' ? json() : xml()
			)
		});
	}, [language]);

	// Update theme
	useEffect(() => {
		viewRef.current?.dispatch({
			effects: themeCompartment.current.reconfigure(
				mode === 'dark' ? oneDark : []
			)
		});
	}, [mode]);

	return (
		<div
			ref={containerRef}
			data-testid={dataTestId}
			style={{ maxHeight, overflow: 'auto' }}
		/>
	);
};

export default CodeViewer;
